import { useQueries } from "@tanstack/react-query";
import axios from "axios";

const REFETCH_INTERVAL_MS = 30 * 1000;
const REQUEST_TIMEOUT_MS = 10 * 1000;

// Almost every listing's api_live_price is the same CoinGecko simple/price
// URL template, differing only in `ids=`. Splitting it lets same-template
// entries share a single batched request instead of one request per coin —
// CoinGecko's free tier rate-limits to ~30 req/min, and 30+ simultaneous
// per-coin requests was tripping that limit for whichever coins fired last.
const splitGeckoUrl = (apiUrl) => {
  try {
    const parsed = new URL(apiUrl);
    const ids = parsed.searchParams.get("ids");
    if (!ids) return null;
    const rest = new URLSearchParams(parsed.search);
    rest.delete("ids");
    return {
      ids,
      template: `${parsed.origin}${parsed.pathname}?${rest.toString()}`,
    };
  } catch (error) {
    return null;
  }
};

const parseCoin = (projectName, coin) => {
  if (!coin) return null;
  let usd_market_cap = coin.usd_market_cap;
  // Fix for MKR (maker): CoinGecko returns 0 market cap — compute from known supply
  if (projectName === "maker" && (!usd_market_cap || usd_market_cap === 0)) {
    usd_market_cap = 977631 * coin.usd;
  }
  return {
    usda: coin.usd,
    usd_24h_change: coin.usd_24h_change,
    usd_market_cap,
    usd_24h_vol: coin.usd_24h_vol,
  };
};

const groupByTemplate = (approved) => {
  const groupMap = new Map();
  const singles = [];

  approved.forEach((res) => {
    const split = res.api_live_price ? splitGeckoUrl(res.api_live_price) : null;
    if (split) {
      if (!groupMap.has(split.template)) {
        groupMap.set(split.template, {
          template: split.template,
          entries: [],
        });
      }
      groupMap.get(split.template).entries.push({
        projectName: res.project_api_name,
        ids: split.ids,
      });
    } else if (res.api_live_price) {
      singles.push(res);
    }
  });

  return { groups: Array.from(groupMap.values()), singles };
};

// Fetches live prices for every "Approved" currency in `crypt`, batching
// entries that share the same CoinGecko URL template into one request each.
//
// `initialSortReady` only waits on those batch (group) queries, each capped
// at REQUEST_TIMEOUT_MS — never on `singles` (currencies with a one-off
// api_live_price). That way a currency that's slow, delisted, or erroring
// only ever affects its own price cell; it can't hold up the rest of the
// list's first paint. "Ready" means settled (success or error), not that
// every coin necessarily has data.
export const useCryptoPrices = (crypt) => {
  const approved = (crypt || []).filter((res) => res.status === "Approved");
  const { groups, singles } = groupByTemplate(approved);

  const groupQueries = useQueries({
    queries: groups.map(({ template, entries }) => {
      const idList = entries
        .map((e) => e.ids)
        .sort()
        .join(",");
      const separator = template.includes("?") ? "&" : "?";
      const batchUrl = `${template}${separator}ids=${idList}`;

      return {
        queryKey: ["crypto-price-batch", template, idList],
        queryFn: async () => {
          const response = await axios.get(batchUrl, {
            timeout: REQUEST_TIMEOUT_MS,
          });
          const result = {};
          entries.forEach(({ projectName }) => {
            const parsed = parseCoin(projectName, response.data[projectName]);
            if (parsed) result[projectName] = parsed;
          });
          return result;
        },
        refetchInterval: REFETCH_INTERVAL_MS,
        retry: 1,
      };
    }),
  });

  const singleQueries = useQueries({
    queries: singles.map((res) => ({
      queryKey: [
        "crypto-price-single",
        res.project_api_name,
        res.api_live_price,
      ],
      queryFn: async () => {
        const response = await axios.get(res.api_live_price, {
          timeout: REQUEST_TIMEOUT_MS,
        });
        const parsed = parseCoin(
          res.project_api_name,
          response.data[res.project_api_name],
        );
        return parsed ? { [res.project_api_name]: parsed } : {};
      },
      refetchInterval: REFETCH_INTERVAL_MS,
      retry: 1,
    })),
  });

  const cryptoPrices = {};
  const marketPrices = {};
  const volume = {};
  const crypto24hchanges = {};
  const circulating_supply = {};

  [...groupQueries, ...singleQueries].forEach((query) => {
    if (!query.data) return;
    Object.entries(query.data).forEach(([projectName, coin]) => {
      cryptoPrices[projectName] = coin.usda;
      marketPrices[projectName] = coin.usd_market_cap;
      volume[projectName] = coin.usd_24h_vol;
      crypto24hchanges[projectName] = coin.usd_24h_change;
      circulating_supply[projectName] = coin.usd_market_cap / coin.usda;
    });
  });

  const initialSortReady =
    groups.length === 0 || groupQueries.every((q) => q.isSuccess || q.isError);

  return {
    cryptoPrices,
    marketPrices,
    volume,
    crypto24hchanges,
    circulating_supply,
    initialSortReady,
  };
};
